'use client';

import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { RootState, useCheckCouponValidationMutation } from '../store';
import { setCoupon } from '../store/slices/orders';
import { getSafeLocation } from '../utils/safeLocation';
import useEventMessage from '../hooks/useEventMessage';
import { toPercentage } from '../utils/shared';
import useEventReferralCode from '../hooks/useEventReferralCode';

const CouponHeadsUp = () => {
  const dispatch = useDispatch();

  const { pathname, searchParams } = getSafeLocation();

  const isBackoffice = pathname.includes('admin');
  const showCouponSubheader = pathname.includes('/events/');
  const couponCode = searchParams.get('coupon');

  const event = useSelector((state: RootState) => state.events.selectedEvent);
  const { coupon } = useSelector((state: RootState) => state.orders);
  const {
    name: referralName, showHeadsUp, isResolving: isReferralNameLoading,
  } = useEventReferralCode(event?.id);

  const [checkCouponValidation, couponValidationResults] = useCheckCouponValidationMutation();

  const validateCoupon = (codeVal: string) => {
    checkCouponValidation({
      businessId: event?.business_id || '',
      eventId: event?.id || '',
      amount: 0,
      couponCode: codeVal,
    });
  };

  useEffect(() => {
    if (couponCode && !coupon && event?.id) {
      validateCoupon(couponCode);
    }
  }, [couponCode, event?.id]);

  useEffect(() => {
    if (couponValidationResults.data?.data) {
      localStorage.setItem(`${event?.id}_coupon`, JSON.stringify(couponValidationResults.data.data));
      dispatch(setCoupon(couponValidationResults.data.data));
    }
  }, [couponValidationResults.data]);

  // Real-time per-event message (event_messages/{eventId}). Stacks in this same
  // top heads-up bar so it reads identically to the referral notice and never
  // overlaps the fixed buy button at the bottom.
  const { active: eventMessageActive, message: eventMessageRaw } = useEventMessage(event?.id);
  const eventMessage = eventMessageActive ? eventMessageRaw.trim() : '';

  const discount = toPercentage(coupon?.coupon?.value ?? 0);
  const hasCoupon = couponCode && coupon;
  const hasReferral = !!referralName && showHeadsUp && !isReferralNameLoading;
  const hasEventMessage = !!eventMessage;

  if (isBackoffice || !showCouponSubheader || (!hasCoupon && !hasReferral && !hasEventMessage) || !event) {
    return null;
  }

  return (
    <div className='fixed top-[74px] left-0 right-0 z-10 mx-[-20px]'>
      {hasCoupon && (
        <div className='bg-primary-500 text-white text-center py-2'>
          DESCUENTO DEL {discount}% APLICADO
        </div>
      )}
      {hasReferral && (
        <div className='bg-primary-500 text-white text-center py-2'>
          REFERIDO: {referralName}
        </div>
      )}
      {hasEventMessage && (
        <div className='bg-primary-500 text-white text-center text-sm py-2 px-6'>
          {eventMessage}
        </div>
      )}
    </div>
  );
};

export default CouponHeadsUp;
